<script>
import { crudMethods } from '@state/helpers'
import DatePicker from 'vue-bootstrap-datetimepicker'

export default {
  name: 'UploadExcelModal',

  components: {
    DatePicker,
  },

  props: {
    mode: {
      type: String,
      default: 'excel',
    },
    value: {
      type: Boolean,
      default: false,
    },

    kodeProyek: {
      type: String,
      required: true,
      default: '',
    },

    dateOptions: {
      type: Object,
      default: () => ({
        format: 'YYYY-MM-DD',
        useCurrent: false,
        showClear: true,
        showClose: true,
      }),
    },
  },

  data() {
    return {
      modalVisible: this.value,

      selectedFile: null,

      isUploading: false,
      isLoadingData: false,

      tglTutupPanjar: '',

      proyek: {},
      tagihans: [],

      checkAll: true,
      panjars: [],
      searchPanjar: '',
      formError: '',
      fetchSequence: 0,
    }
  },

  computed: {
    isManual() {
      return this.mode === 'manual'
    },
    selectedPanjars() {
      return this.panjars.filter((item) => item.checked)
    },
    filteredPanjars() {
      const query = this.searchPanjar.trim().toLowerCase()
      return this.panjars.filter((item) =>
        `${item.id} ${item.uraian} ${item.batch || ''}`
          .toLowerCase()
          .includes(query)
      )
    },
    totalManual() {
      return this.selectedPanjars.reduce(
        (total, item) => total + Number(item.nominal || 0),
        0
      )
    },
    availableQuota() {
      return this.tagihans
        .filter((item) => item.checked)
        .reduce(
          (total, item) =>
            total + Math.max(0, Number(item.beban_sph_vendor_tersedia || 0)),
          0
        )
    },
    manualValidation() {
      if (!this.selectedPanjars.length) return 'Pilih minimal satu panjar.'
      if (
        this.selectedPanjars.some(
          (item) =>
            !Number.isSafeInteger(Number(item.nominal)) ||
            Number(item.nominal) <= 0
        )
      ) {
        return 'Isi nominal setiap panjar yang dipilih dalam Rupiah bulat, lebih dari 0.'
      }
      if (
        this.selectedPanjars.some((item) => Number(item.nominal) > item.sisa)
      ) {
        return 'Nominal tidak boleh melebihi sisa panjar.'
      }
      if (this.totalManual > this.availableQuota)
        return 'Total nominal melebihi sisa kuota tagihan yang dipilih.'
      return ''
    },
    isFormInvalid() {
      const hasSelectedTagihan = this.tagihans.some((item) => item.checked)

      return (
        (this.isManual ? !!this.manualValidation : !this.selectedFile) ||
        !this.tglTutupPanjar ||
        !hasSelectedTagihan ||
        this.isUploading ||
        this.isLoadingData ||
        (this.isManual &&
          this.tagihans.some(
            (item) =>
              item.checked &&
              (!Number.isSafeInteger(Number(item.nominal)) ||
                Number(item.nominal) < 0)
          ))
      )
    },
  },

  watch: {
    value(val) {
      this.modalVisible = val

      if (val && this.kodeProyek) {
        this.fetchDataTagihan()
      }
    },

    modalVisible(val) {
      this.$emit('input', val)
    },
  },

  methods: {
    ...crudMethods,
    closeModal() {
      this.modalVisible = false
    },
    inputNominal(item, event) {
      const input = event.target
      const digitsBeforeCursor = input.value
        .slice(0, input.selectionStart)
        .replace(/\D/g, '').length
      const digits = input.value.replace(/\D/g, '')
      item.nominal = digits === '' ? '' : Number(digits)
      const formatted = digits === '' ? '' : this.formatRupiah(item.nominal)
      input.value = formatted
      let cursor = 0
      let count = 0
      while (cursor < formatted.length && count < digitsBeforeCursor) {
        if (/\d/.test(formatted[cursor])) count += 1
        cursor += 1
      }
      input.setSelectionRange(cursor, cursor)
    },

    // Mengambil data plan tagihan dari backend
    async fetchDataTagihan() {
      const sequence = ++this.fetchSequence
      this.isLoadingData = true
      this.tagihans = []
      this.proyek = {}
      this.formError = ''
      this.panjars = []

      try {
        const [response, panjarResponse] = await Promise.all([
          this.listData({
            url: `web/transaksi/proyek/${this.kodeProyek}/panjar-ikhtisar/tagihan`,
          }),
          this.isManual
            ? this.listData({
                url: `web/transaksi/proyek/${this.kodeProyek}/panjar`,
              })
            : Promise.resolve(null),
        ])
        if (sequence !== this.fetchSequence) return
        if (!response || (this.isManual && !panjarResponse))
          throw new Error('Data tidak tersedia')
        const data = response.data.data

        if (data !== null) {
          this.proyek = data

          this.tagihans = (data.tagihan || []).map((item) => ({
            no: item.no,

            total_tagihan: item.total_tagihan || 0,

            // Data beban SPH Vendor
            beban_sph_vendor: item.beban_sph_vendor || 0,

            // Plafon SPH Vendor yang masih tersedia
            beban_sph_vendor_tersedia: item.beban_sph_vendor_tersedia || 0,

            tanggal_awal: item.tanggal_awal,
            tanggal_akhir: item.tanggal_akhir,

            checked: true,

            // TETAP menggunakan total tagihan
            // sebagai nominal bank
            nominal: item.total_tagihan || 0,

            faktur: item.faktur || '',
          }))

          this.checkAll = true
        }
        if (this.isManual) {
          const raw = panjarResponse.data.data
          const items = Array.isArray(raw) ? raw : (raw && raw.data) || []
          this.panjars = items
            .map((item) => {
              const total =
                Number(item.nilai_dpp || 0) *
                  (1 + Number(item.ppn || 0) / 100) +
                (item.biaya_lains || []).reduce(
                  (sum, biaya) => sum + Number(biaya.biaya || 0),
                  0
                )
              const used = (item.ikhtisars || []).reduce(
                (sum, detail) => sum + Number(detail.nominal || 0),
                0
              )
              return {
                id: item.id,
                uraian: item.uraian,
                batch: item.batch,
                total,
                sisa: Math.max(0, total - used),
                checked: false,
                nominal: '',
              }
            })
            .filter((item) => item.sisa >= 1)
        }
      } catch (error) {
        if (sequence === this.fetchSequence)
          this.formError =
            'Gagal memuat data tagihan atau panjar. Tutup form lalu coba lagi.'
      } finally {
        if (sequence === this.fetchSequence) this.isLoadingData = false
      }
    },

    // Toggle semua checkbox
    toggleAll(checked) {
      this.tagihans.forEach((item) => {
        item.checked = checked
      })
    },

    // Toggle checkbox per row
    toggleRow() {
      this.checkAll =
        this.tagihans.length > 0 && this.tagihans.every((item) => item.checked)
    },

    // Format tanggal
    dateFormat(date) {
      if (!date) return '-'

      try {
        const d = new Date(date)

        return d.toLocaleDateString('id-ID', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      } catch (e) {
        return '-'
      }
    },

    // Format Rupiah
    formatRupiah(value) {
      if (value === null || value === undefined || value === '') {
        return '0'
      }

      return Number(value)
        .toFixed(0)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },

    // Handle file
    handleFileChange(file) {
      this.selectedFile = file
    },

    // Proses upload
    submitUpload() {
      if (this.isFormInvalid) return

      this.isUploading = true
      this.formError = ''

      const payload = new FormData()

      if (!this.isManual) payload.append('file_excel', this.selectedFile)
      if (this.isManual) {
        this.selectedPanjars.forEach((item, index) => {
          payload.append(`details[${index}][panjar_id]`, item.id)
          payload.append(`details[${index}][nominal]`, item.nominal)
        })
      }

      payload.append('tgl_tutup_panjar', this.tglTutupPanjar)

      let counter = 0

      this.tagihans.forEach((item) => {
        if (item.checked) {
          payload.append(`tagihans[${counter}][no]`, item.no)

          payload.append(`tagihans[${counter}][faktur]`, item.faktur || '')

          payload.append(`tagihans[${counter}][nominal]`, item.nominal || 0)

          counter++
        }
      })

      this.$emit('submit-upload', {
        formData: payload,

        onSuccess: () => {
          this.isUploading = false
          this.modalVisible = false
          this.resetForm()
        },

        onError: (error) => {
          this.isUploading = false
          if (this.isManual) {
            const data = error && error.response && error.response.data
            const errors = data && data.errors
            this.formError = errors
              ? Object.values(errors)
                  .reduce((all, messages) => all.concat(messages), [])
                  .join(' ')
              : (data && data.message) ||
                (error && error.message) ||
                'Gagal menyimpan ikhtisar manual.'
          }
        },
      })
    },

    // Reset form
    resetForm() {
      this.fetchSequence += 1
      this.isLoadingData = false
      this.selectedFile = null
      this.tglTutupPanjar = ''
      this.tagihans = []
      this.proyek = {}
      this.isUploading = false
      this.checkAll = true
      this.panjars = []
      this.searchPanjar = ''
      this.formError = ''

      if (this.$refs.fileInput) {
        this.$refs.fileInput.reset()
      }
    },
  },
}
</script>

<template>
  <b-modal
    v-model="modalVisible"
    centered
    no-close-on-backdrop
    :title="isManual ? 'Tambah Ikhtisar Manual' : 'Upload Rekonsiliasi Excel'"
    :size="isManual ? 'xl' : 'lg'"
    :no-close-on-esc="isUploading"
    :hide-header-close="isUploading"
    header-bg-variant="light"
    @hidden="resetForm"
  >
    <div class="p-1">
      <div v-if="formError" class="alert alert-danger" role="alert">{{
        formError
      }}</div>
      <!-- Loading -->
      <div v-if="isLoadingData" class="text-center my-4">
        <b-spinner variant="primary" small></b-spinner>

        <p class="text-muted small mt-1">
          Memuat data tagihan proyek...
        </p>
      </div>

      <fieldset v-else class="row border-0 p-0 m-0" :disabled="isUploading">
        <!-- ========================================= -->
        <!-- TANGGAL TUTUP PANJAR -->
        <!-- ========================================= -->

        <div class="form-group col-sm-12">
          <label class="font-weight-bold text-dark">
            Tanggal Tutup Panjar

            <span
              v-if="!tglTutupPanjar"
              class="text-danger font-size-11 font-italic"
            >
              *Harus Diisi
            </span>
          </label>

          <date-picker
            v-model="tglTutupPanjar"
            :config="dateOptions"
            class="rounded-0"
            placeholder="Pilih Tanggal Tutup"
          ></date-picker>
        </div>

        <!-- ========================================= -->
        <!-- INFORMASI PROYEK -->
        <!-- ========================================= -->

        <div v-if="tagihans.length > 0" class="col-sm-12 mb-2">
          <table class="table table-sm table-bordered bg-light info-table">
            <tbody>
              <tr>
                <td class="font-weight-bold text-muted width-25">
                  Nama Proyek
                </td>

                <td>
                  {{ proyek.nama_proyek }}
                </td>
              </tr>

              <tr>
                <td class="font-weight-bold text-muted">
                  Mekanisme / Nilai
                </td>

                <td>
                  <span class="badge badge-info mr-2">
                    {{ proyek.mekanisme_pembayaran }}
                  </span>

                  <span class="font-weight-bold text-primary">
                    Rp
                    {{ formatRupiah(proyek.nilai_kontrak_belum_ppn) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ========================================= -->
        <!-- TABEL TAGIHAN -->
        <!-- ========================================= -->

        <div class="col-sm-12 mb-3">
          <label class="font-weight-bold text-dark mb-1">
            Pilih Plan Tagihan & Alokasi Nominal

            <span class="text-danger font-size-11 font-italic">
              *Minimal centang 1
            </span>
          </label>

          <div class="table-responsive border" style="max-height: 250px;">
            <table class="table table-sm table-hover mb-0 grid-table">
              <thead class="bg-light">
                <tr>
                  <!-- CHECK -->
                  <th class="text-center align-middle" style="width: 5%">
                    <b-form-checkbox
                      v-model="checkAll"
                      @change="toggleAll"
                    ></b-form-checkbox>
                  </th>

                  <!-- TERMIN / BULAN -->
                  <th class="align-middle" style="width: 10%">
                    Termin
                  </th>

                  <!-- TOTAL -->
                  <th class="align-middle text-right" style="width: 20%">
                    Total Tagihan
                  </th>

                  <!-- PLAFON -->
                  <th class="align-middle text-right" style="width: 25%">
                    Plafon SPH Tersedia
                  </th>

                  <!-- NOMINAL -->
                  <th class="align-middle" style="width: 20%">
                    Nominal Bank
                  </th>

                  <!-- FAKTUR -->
                  <th class="align-middle" style="width: 20%">
                    No. Faktur
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr v-if="tagihans.length === 0">
                  <td colspan="6" class="text-center py-3 text-muted">
                    Tidak ada data plan tagihan.
                  </td>
                </tr>

                <tr
                  v-for="(item, index) in tagihans"
                  :key="index"
                  :class="{
                    'table-disabled-row': !item.checked,
                  }"
                >
                  <!-- CHECKBOX -->

                  <td class="text-center align-middle">
                    <b-form-checkbox
                      v-model="item.checked"
                      @change="toggleRow"
                    ></b-form-checkbox>
                  </td>

                  <!-- TERMIN -->

                  <td class="align-middle text-nowrap">
                    <span class="font-weight-bold">
                      {{
                        proyek.mekanisme_pembayaran === 'MRC'
                          ? 'Bln ' + item.no
                          : proyek.mekanisme_pembayaran === 'OTC'
                          ? 'Term ' + item.no
                          : 'No ' + item.no
                      }}
                    </span>
                  </td>

                  <!-- TOTAL TAGIHAN -->

                  <td
                    class="text-right align-middle font-weight-bold text-secondary"
                  >
                    Rp
                    {{ formatRupiah(item.total_tagihan) }}
                  </td>

                  <!-- PLAFON SPH TERSEDIA -->

                  <td class="text-right align-middle">
                    <div class="plafon-sph">
                      <span class="plafon-label">
                        Tersedia
                      </span>

                      <span class="plafon-value">
                        Rp
                        {{ formatRupiah(item.beban_sph_vendor_tersedia) }}
                      </span>
                    </div>
                  </td>

                  <!-- NOMINAL BANK -->

                  <td class="align-middle">
                    <input
                      :value="
                        item.nominal === '' ? '' : formatRupiah(item.nominal)
                      "
                      type="text"
                      inputmode="numeric"
                      class="form-control form-control-sm table-input text-right font-weight-bold"
                      :disabled="!item.checked"
                      placeholder="0"
                      @input="inputNominal(item, $event)"
                    />
                  </td>

                  <!-- NO FAKTUR -->

                  <td class="align-middle">
                    <input
                      v-model="item.faktur"
                      type="text"
                      class="form-control form-control-sm table-input"
                      :disabled="!item.checked"
                      placeholder="Isi No Faktur"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ========================================= -->
        <!-- FILE EXCEL -->
        <!-- ========================================= -->

        <div v-if="!isManual" class="form-group col-sm-12 mb-0">
          <label class="font-weight-bold text-dark">
            File Excel (.xlsx, .xls, .csv)

            <span
              v-if="!selectedFile"
              class="text-danger font-size-11 font-italic"
            >
              *Wajib diunggah
            </span>
          </label>

          <b-form-file
            ref="fileInput"
            placeholder="Pilih file excel / drop di sini..."
            drop-placeholder="Drop file di sini..."
            accept=".xlsx, .xls, .csv"
            class="rounded-0"
            @input="handleFileChange"
          ></b-form-file>
        </div>
        <div v-else class="col-sm-12">
          <h6>Pilih Panjar &amp; Input Nominal</h6>
          <p class="text-muted"
            >Panjar yang masih memiliki sisa tersedia di bawah. Nominal
            dialokasikan ke kuota tagihan yang dipilih dan disimpan sebagai
            DRAFT.</p
          >
          <input
            v-model="searchPanjar"
            class="form-control mb-2"
            placeholder="Cari nomor, uraian, atau batch panjar"
            aria-label="Cari panjar"
          />
          <div
            class="table-responsive"
            style="max-height: 400px; overflow-y: auto"
          >
            <table class="table table-sm table-bordered">
              <thead
                ><tr
                  ><th>Pilih</th><th>No. Panjar</th><th>Uraian / Batch</th
                  ><th>Sisa Panjar</th><th>Nominal Ikhtisar (Rp)</th></tr
                ></thead
              >
              <tbody>
                <tr v-for="item in filteredPanjars" :key="item.id">
                  <td
                    ><input
                      v-model="item.checked"
                      type="checkbox"
                      :aria-label="`Pilih panjar ${item.id}`"
                  /></td>
                  <td>{{ item.id }}</td>
                  <td
                    >{{ item.uraian
                    }}<small class="d-block text-muted"
                      >Batch: {{ item.batch || '-' }}</small
                    ></td
                  >
                  <td class="text-right text-nowrap"
                    >Rp {{ formatRupiah(item.sisa) }}</td
                  >
                  <td>
                    <input
                      :value="
                        item.nominal === '' ? '' : formatRupiah(item.nominal)
                      "
                      type="text"
                      inputmode="numeric"
                      :disabled="!item.checked"
                      :aria-label="`Nominal panjar ${item.id}`"
                      class="form-control form-control-sm text-right"
                      :class="{
                        'is-invalid':
                          item.checked && Number(item.nominal) > item.sisa,
                      }"
                      @input="inputNominal(item, $event)"
                    />
                  </td>
                </tr>
                <tr v-if="!filteredPanjars.length"
                  ><td colspan="5" class="text-center text-muted"
                    >Tidak ada panjar tersedia yang sesuai.</td
                  ></tr
                >
              </tbody>
            </table>
          </div>
          <div class="bg-light p-3 mt-2">
            <div
              >Panjar dipilih:
              <strong>{{ selectedPanjars.length }}</strong></div
            >
            <div
              >Total nominal:
              <strong>Rp {{ formatRupiah(totalManual) }}</strong></div
            >
            <div
              >Sisa kuota tagihan dipilih:
              <strong>Rp {{ formatRupiah(availableQuota) }}</strong></div
            >
            <small v-if="manualValidation" class="text-danger d-block mt-1">{{
              manualValidation
            }}</small>
          </div>
        </div>
      </fieldset>
    </div>

    <!-- ========================================= -->
    <!-- FOOTER -->
    <!-- ========================================= -->

    <template v-slot:modal-footer>
      <div class="w-100 text-right">
        <b-button
          class="mr-2 rounded-0"
          type="button"
          variant="outline-secondary"
          size="sm"
          :disabled="isUploading"
          @click="closeModal"
        >
          Batal
        </b-button>

        <b-button
          type="button"
          variant="primary"
          size="sm"
          class="rounded-0 px-3"
          :disabled="isFormInvalid"
          @click="submitUpload"
        >
          <b-spinner v-if="isUploading" small class="mr-1"></b-spinner>

          <i v-else class="fa fa-save mr-1"></i>

          {{ isUploading ? 'Menyimpan...' : 'Simpan Rekonsiliasi' }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<style scoped>
.font-size-11 {
  font-size: 11px;
}

.font-italic {
  font-style: italic;
}

.width-25 {
  width: 25%;
}

.info-table {
  font-size: 12px;
}

.grid-table {
  font-size: 12px;
}

/* =========================================
   TABLE INPUT
   ========================================= */

.table-input {
  height: 28px;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 0;
}

/* =========================================
   PLAFON SPH
   ========================================= */

.plafon-sph {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
}

.plafon-label {
  font-size: 9px;
  color: #8a929b;
  margin-bottom: 2px;
}

.plafon-value {
  font-size: 12px;
  font-weight: 700;
  color: #198754;
  white-space: nowrap;
}

/* =========================================
   DISABLED ROW
   ========================================= */

.table-disabled-row td {
  background-color: #fcfcfc !important;
  color: #a0a0a0;
}

.table-disabled-row .plafon-value {
  color: #a0a0a0;
}

/* =========================================
   FILE INPUT
   ========================================= */

::v-deep .custom-file-label {
  border-radius: 0 !important;
}

::v-deep .custom-file-label::after {
  border-radius: 0 !important;
}
</style>
